import { createContext, useCallback, useContext, useState, useEffect, useRef } from "react"
import { api } from "../lib/api"
import type { InstagramAccount } from "../components/settings/AccountsTab"
import { PrefetchedOrgsContext, type OrganizationWithAccounts } from "./organizations"
import { isAtLeastPlan } from "../lib/plan"
export type { OrganizationWithAccounts } from "./organizations"

type AccountContextType = {
  organizations: OrganizationWithAccounts[]
  // 複数選択
  selectedAccountIds: number[]
  setSelectedAccountIds: (ids: number[]) => void
  // 複数期間指定中は複数アカウント選択を禁止する
  singleAccountOnly: boolean
  setSingleAccountOnly: (v: boolean) => void
  // shim（既存コンポーネントの互換維持）
  selectedAccountId: number | null
  setSelectedAccountId: (id: number) => void
  selectedAccount: InstagramAccount | null
  selectedOrganizationId: number | null
  setSelectedOrganizationId: (id: number) => void
  selectedOrganizationRole: "member" | "owner" | null
  refreshOrganizations: () => Promise<void>
  currentPermission: "view" | "edit" | null
}

const AccountContext = createContext<AccountContextType>({
  organizations: [],
  selectedAccountIds: [],
  setSelectedAccountIds: () => {},
  singleAccountOnly: false,
  setSingleAccountOnly: () => {},
  selectedAccountId: null,
  setSelectedAccountId: () => {},
  selectedAccount: null,
  selectedOrganizationId: null,
  setSelectedOrganizationId: () => {},
  selectedOrganizationRole: null,
  refreshOrganizations: async () => {},
  currentPermission: null,
})

const LS_ORG_KEY = "lyto.selectedOrganizationId"
// アカウント選択は sessionStorage（タブ/セッション単位でリセットし、跨セッションで無効な
// 横断組み合わせが復元される事態を防ぐ）。組織選択は localStorage のまま。
const SS_ACC_KEY = "lyto.selectedAccountId"

// 旧キー（insighta.*）から新キー（lyto.*）へのマイグレーション
const OLD_LS_ORG_KEY = "insighta.selectedOrganizationId"
const OLD_LS_ACC_KEY = "insighta.selectedAccountId"
;(() => {
  const orgV = localStorage.getItem(OLD_LS_ORG_KEY)
  if (orgV !== null) {
    localStorage.setItem(LS_ORG_KEY, orgV)
    localStorage.removeItem(OLD_LS_ORG_KEY)
  }
  // アカウント選択は旧 insighta.* キー・移行前の localStorage 実装のどちらからでも sessionStorage へ移行
  for (const legacyKey of [OLD_LS_ACC_KEY, SS_ACC_KEY]) {
    const accV = localStorage.getItem(legacyKey)
    if (accV !== null) {
      if (sessionStorage.getItem(SS_ACC_KEY) === null) sessionStorage.setItem(SS_ACC_KEY, accV)
      localStorage.removeItem(legacyKey)
    }
  }
})()

const readStoredIds = (key: string): number[] => {
  const v = sessionStorage.getItem(key)
  if (!v) return []
  const parsed = JSON.parse(v)
  // 旧形式（単数の数値文字列 "20"）は配列に変換
  return Array.isArray(parsed) ? parsed : [parsed]
}

const readStoredId = (key: string): number | null => {
  const v = localStorage.getItem(key)
  return v ? Number(v) : null
}

const pickDefaultAccount = (accounts: InstagramAccount[]): number | null =>
  accounts.find(a => a.connected && a.token_status === "valid")?.id
  ?? accounts[0]?.id
  ?? null

export const AccountProvider = ({ children }: { children: React.ReactNode }) => {
  const prefetchedOrgs = useContext(PrefetchedOrgsContext)
  const hasInitialized = useRef(false)
  const [organizations, setOrganizations] = useState<OrganizationWithAccounts[]>([])
  const [selectedAccountIds, setSelectedAccountIdsState] = useState<number[]>([])
  const [selectedOrganizationId, setSelectedOrganizationIdState] = useState<number | null>(null)
  const [singleAccountOnly, setSingleAccountOnly] = useState(false)

  const applyOrganizationsData = useCallback((data: OrganizationWithAccounts[], preferOrgId?: number | null, preferAccIds?: number[]) => {
    setOrganizations(data)

    const savedOrgId = preferOrgId !== undefined ? preferOrgId : readStoredId(LS_ORG_KEY)
    const org = (savedOrgId !== null ? data.find(o => o.id === savedOrgId) : null) ?? data[0]

    if (org) {
      setSelectedOrganizationIdState(org.id)
      localStorage.setItem(LS_ORG_KEY, String(org.id))

      const allAccounts = data.flatMap(o => o.accounts)
      const savedAccIds = preferAccIds !== undefined ? preferAccIds : readStoredIds(SS_ACC_KEY)
      const validIds = savedAccIds.filter(id => allAccounts.some(a => a.id === id))
      const defaultId = pickDefaultAccount(org.accounts)

      // 横断可能プラン優先クランプ：無効な横断組み合わせ（cross_account_min_plan未満混在の2件以上）は
      // 横断可能なアカウントだけを残し、無ければ先頭1件の単独表示にフォールバックする
      const accountOf = (id: number) => allAccounts.find(a => a.id === id)
      const crossAccountEligibleIds = validIds.filter(id => {
        const acc = accountOf(id)
        return !!acc && isAtLeastPlan(acc.plan, acc.features.cross_account_min_plan)
      })
      const ids =
        crossAccountEligibleIds.length > 0 ? crossAccountEligibleIds
        : validIds.length > 0 ? [ validIds[0] ]
        : defaultId !== null ? [ defaultId ] : []

      setSelectedAccountIdsState(ids)
      if (ids.length > 0) sessionStorage.setItem(SS_ACC_KEY, JSON.stringify(ids))
      else sessionStorage.removeItem(SS_ACC_KEY)
    }
  }, [])

  const loadOrganizations = useCallback(async (preferOrgId?: number | null, preferAccIds?: number[]) => {
    const data = await api.get<OrganizationWithAccounts[]>("/organizations")
    applyOrganizationsData(data, preferOrgId, preferAccIds)
  }, [applyOrganizationsData])

  useEffect(() => {
    if (hasInitialized.current) return
    hasInitialized.current = true

    if (prefetchedOrgs !== null) {
      applyOrganizationsData(prefetchedOrgs)
    } else {
      loadOrganizations().catch(() => {})
    }
  }, [prefetchedOrgs, applyOrganizationsData, loadOrganizations])

  const setSelectedOrganizationId = useCallback((id: number) => {
    const org = organizations.find(o => o.id === id)
    if (!org) return
    setSelectedOrganizationIdState(id)
    localStorage.setItem(LS_ORG_KEY, String(id))
    const accountId = pickDefaultAccount(org.accounts)
    const ids = accountId !== null ? [accountId] : []
    setSelectedAccountIdsState(ids)
    sessionStorage.setItem(SS_ACC_KEY, JSON.stringify(ids))
  }, [organizations])

  const setSelectedAccountIds = useCallback((ids: number[]) => {
    setSelectedAccountIdsState(ids)
    sessionStorage.setItem(SS_ACC_KEY, JSON.stringify(ids))

    const firstId = ids[0]
    if (firstId !== undefined) {
      const org = organizations.find(o => o.accounts.some(a => a.id === firstId))
      if (org) {
        setSelectedOrganizationIdState(org.id)
        localStorage.setItem(LS_ORG_KEY, String(org.id))
      }
    }
  }, [organizations])

  // shim: 既存コンポーネントの互換維持
  const selectedAccountId = selectedAccountIds[0] ?? null
  const setSelectedAccountId = useCallback((id: number) => {
    setSelectedAccountIds([id])
  }, [setSelectedAccountIds])

  const refreshOrganizations = useCallback(async () => {
    const data = await api.get<OrganizationWithAccounts[]>("/organizations")
    applyOrganizationsData(data, selectedOrganizationId, selectedAccountIds)
  }, [applyOrganizationsData, selectedOrganizationId, selectedAccountIds])

  const selectedAccount =
    organizations.flatMap(o => o.accounts).find(a => a.id === selectedAccountId) ?? null

  const currentPermission = selectedAccount?.permission_level ?? null

  const selectedOrganizationRole =
    organizations.find(o => o.id === selectedOrganizationId)?.role ?? null

  return (
    <AccountContext.Provider value={{
      organizations,
      selectedAccountIds,
      setSelectedAccountIds,
      singleAccountOnly,
      setSingleAccountOnly,
      selectedAccountId,
      setSelectedAccountId,
      selectedAccount,
      selectedOrganizationId,
      setSelectedOrganizationId,
      selectedOrganizationRole,
      refreshOrganizations,
      currentPermission,
    }}>
      {children}
    </AccountContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export const useSelectedAccount = () => useContext(AccountContext)
